/** Right side-drawer for record detail/edit (MASTER §6, Radix Dialog). */
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn.js';

export function SideDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  headerAccessory,
  footer,
  children,
  width = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  headerAccessory?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: 'md' | 'lg' | 'xl';
}): ReactNode {
  const w = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' }[width];
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-surface shadow-drawer',
            'data-[state=open]:animate-slide-in-right focus:outline-none',
            w,
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="font-display text-xl font-semibold text-fg">
                {title}
              </Dialog.Title>
              {subtitle ? (
                <Dialog.Description className="mt-0.5 text-sm text-muted">
                  {subtitle}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <div className="flex items-center gap-1">
              {headerAccessory}
              <Dialog.Close
                aria-label="Close"
                className="grid h-9 w-9 cursor-pointer place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-fg"
              >
                <X className="h-5 w-5" aria-hidden />
              </Dialog.Close>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <div className="border-t border-border bg-surface px-5 py-3.5">{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
